import { useEffect, useState } from "react";
import { Check, Loader2, Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { startProCheckout } from "@/lib/billing";
import { track } from "@/lib/posthog";
import { PROMO } from "@/lib/promo";
import TrackOnView from "@/components/TrackOnView";

type Interval = "month" | "year";

const FEATURES = {
  free: [
    "1 New Design Project Per Month",
    "1K Resolution Render Images",
    "3 Iteration Tokens Per Month",
    "AI Product Shopping Suggestions",
    "Basic Support",
    "Unlimited Saved Projects",
  ],
  pro: [
    "5 New Design Projects Per Month",
    "1K Resolution Render Images",
    "15 Iteration Tokens Per Month",
    "AI Product Shopping Suggestions",
    "Priority Support",
    "Unlimited Saved Projects",
  ],
  ultra: [
    "20 New Design Projects Per Month",
    "2K Resolution Render Images",
    "60 Iteration Tokens Per Month",
    "AI Product Shopping Suggestions",
    "Priority Support",
    "Unlimited Saved Projects",
  ],
} as const;

// Yearly = 20% off, billed once a year (shown as the monthly equivalent).
const PRICES = {
  pro: { month: "6.99", year: "5.59" },
  ultra: { month: "20.99", year: "16.79" },
} as const;

interface Props {
  onPlanChange?: () => void;
}

const PricingPlans = ({ onPlanChange }: Props) => {
  const { toast } = useToast();
  const { plan, isPro, billingInterval, refresh, loading, promoEligible } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep links from huelo.co: ?plan=free|pro|ultra&billing=monthly|yearly
  const billingParam = searchParams.get("billing");
  const [interval, setInterval] = useState<Interval>(
    billingParam === "yearly" || billingParam === "year" ? "year" : "month",
  );
  const planParam = searchParams.get("plan");
  const highlighted = planParam === "free" || planParam === "pro" || planParam === "ultra" ? planParam : null;
  const [busy, setBusy] = useState<null | "pro" | "ultra" | "portal">(null);

  // If returning from Stripe Checkout, refresh subscription state.
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    let ctx: Record<string, unknown> = {};
    try { ctx = JSON.parse(sessionStorage.getItem("huelo_checkout_ctx") ?? "{}"); } catch { /* ignore */ }
    if (checkout) {
      track(checkout === "success" ? "checkout_completed" : "checkout_cancelled", ctx);
      try { sessionStorage.removeItem("huelo_checkout_ctx"); } catch { /* ignore */ }
    }
    if (checkout === "success") {
      toast({ title: "Subscription active", description: "Welcome to your new Huelo plan." });
      void refresh().then(() => onPlanChange?.());
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    } else if (checkout === "cancelled") {
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refresh, toast, onPlanChange]);

  const startCheckout = async (tier: "pro" | "ultra") => {
    setBusy(tier);
    try {
      track("upsell_clicked", { placement: "pricing", tier, interval, promo_eligible: promoEligible, plan });
      const url = await startProCheckout(interval, tier, "pricing");
      window.open(url, "_blank");
    } catch (e: any) {
      toast({ title: "Checkout failed", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        "create-portal",
        { body: {} },
      );
      if (error || !data?.url) throw new Error(error?.message || "Couldn't open portal");
      window.open(data.url, "_blank");
    } catch (e: any) {
      toast({ title: "Couldn't open billing portal", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const showPromo = promoEligible && !isPro && interval === "month";
  const suffix = interval === "year" ? "/mo billed yearly" : "/month";

  const paidCta = (tier: "pro" | "ultra") => {
    const btn =
      "w-full rounded-full bg-background text-foreground px-6 py-3 text-sm font-semibold hover:opacity-95 transition-opacity inline-flex items-center justify-center gap-2 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:hover:opacity-100";
    if (plan === tier) {
      return (
        <button onClick={openPortal} disabled={busy !== null} className={btn}>
          {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Manage subscription
        </button>
      );
    }
    if (isPro) {
      // Already on the other paid tier — switch plans in the billing portal.
      return (
        <button onClick={openPortal} disabled={busy !== null} className={btn}>
          {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Switch in portal
        </button>
      );
    }
    return (
      <button onClick={() => startCheckout(tier)} disabled={busy !== null || loading} className={btn}>
        {busy === tier ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {tier === "pro" && showPromo ? PROMO.cta : `Subscribe to ${tier === "pro" ? "Pro" : "Ultra"}`}
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Interval toggle */}
      <div className="flex items-center justify-center gap-3">
        <span
          className={`text-sm font-medium ${
            interval === "month" ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          Monthly
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={interval === "year"}
          onClick={() => setInterval(interval === "month" ? "year" : "month")}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            interval === "year" ? "bg-foreground" : "bg-muted"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-background transition-transform ${
              interval === "year" ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
        <span
          className={`text-sm font-medium ${
            interval === "year" ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          Yearly
        </span>
        <span className="inline-flex items-center rounded-full bg-success px-2.5 py-0.5 text-xs font-semibold text-success-foreground">
          20% off
        </span>
      </div>

      {/* Tier cards */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 ${showPromo ? "md:pt-16" : ""}`}>
        <PlanCard
          eyebrow="Free"
          subtitle="Try Huelo on one room a month."
          price="$0"
          priceSuffix="/month"
          variant="dark"
          features={FEATURES.free}
          cta={
            <button
              disabled
              className="w-full rounded-full bg-background text-foreground px-6 py-3 text-sm font-semibold disabled:opacity-100"
            >
              {isPro ? "Downgrade in portal" : "Current plan"}
            </button>
          }
        />
        <PlanCard
          eyebrow="Pro"
          subtitle="For a few rooms each month."
          price={showPromo ? `$${PROMO.price}` : `$${PRICES.pro[interval]}`}
          priceSuffix={showPromo ? " first month" : suffix}
          promoNote={showPromo ? `Then $${PROMO.regularPrice}/month · Cancel anytime` : undefined}
          strike={showPromo ? `$${PROMO.regularPrice}` : undefined}
          variant="dark"
          features={FEATURES.pro}
          cta={paidCta("pro")}
        />
        <PlanCard
          eyebrow="Ultra"
          subtitle="Sharper renders and room to explore."
          price={`$${PRICES.ultra[interval]}`}
          priceSuffix={suffix}
          variant="pro"
          features={FEATURES.ultra}
          cta={paidCta("ultra")}
        />
      </div>

      {isPro && billingInterval && (
        <p className="text-center text-xs text-muted-foreground">
          You're on Huelo {plan === "ultra" ? "Ultra" : "Pro"} (billed {billingInterval === "year" ? "yearly" : "monthly"}).
        </p>
      )}
    </div>
  );
};

interface PlanCardProps {
  eyebrow: string;
  subtitle: string;
  price: string;
  priceSuffix: string;
  variant: "dark" | "pro";
  features: readonly string[];
  cta: React.ReactNode;
  promoNote?: string;
  strike?: string;
}

const PlanCard = ({ eyebrow, subtitle, price, priceSuffix, variant, features, cta, promoNote, strike }: PlanCardProps) => {
  const cardBase =
    "rounded-lg p-7 flex flex-col justify-between min-h-[360px] shadow-sm";
  const cardClass =
    variant === "pro"
      ? `${cardBase} bg-gradient-cta text-primary-foreground shadow-elegant`
      : `${cardBase} bg-foreground text-background`;

  return (
    <div className={`relative flex flex-col gap-5 ${promoNote ? "pt-16 md:pt-0" : ""}`}>
      {promoNote && (
        <TrackOnView
          event="upsell_viewed"
          properties={{ placement: "pricing", promo_eligible: true }}
          className="absolute inset-x-0 top-0 md:-top-16 min-h-20 rounded-lg bg-primary text-primary-foreground px-4 pt-3 pb-7 text-center"
        >
          <p className="text-sm font-semibold">{PROMO.badge}</p>
          <p className="mt-1 text-xs leading-4">{promoNote}</p>
        </TrackOnView>
      )}
      <div className={`relative ${cardClass}`}>
        <div>
          <h3 className="text-2xl font-display font-semibold">{eyebrow}</h3>
          <p className="text-sm opacity-80 mt-1">{subtitle}</p>
          <div className="mt-8 flex items-baseline gap-1.5">
            {strike && <span className="text-xl opacity-60 line-through">{strike}</span>}
            <span className="text-5xl font-display font-bold tracking-tight">{price}</span>
            {priceSuffix && <span className="text-sm opacity-80">{priceSuffix}</span>}
          </div>
        </div>
        <div className="mt-8">{cta}</div>
      </div>
      <ul className="space-y-2.5 px-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-foreground">
            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PricingPlans;
